import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from 'axios';
import { BookOpen, Phone, Mail, Search, X, Calendar, Filter, Users, Clock } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import { getDisplayName } from '../utils/displayName';

interface DirectoryUser {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string | null;
  phone: string | null;
  shared_teams: Array<{
    team_id: number;
    team_name: string;
    join_date?: string;
    is_active?: boolean;
  }>;
}

interface PlayerLineup {
  lineup_id: number;
  match_id: number;
  match_date: string;
  opponent_name: string;
  position_number: number;
  player1_id: number | null;
  player1_name: string | null;
  player2_id: number | null;
  player2_name: string | null;
  score: string | null;
  won: boolean | null;
  team_id: number;
  team_name: string;
}

const DirectoryPage: React.FC = () => {
  const { user } = useAuth();
  const { teams } = useTeam();
  const { error: showError } = useToast();
  const directoryEnabled = useFeatureFlag('directory-feature', false);
  
  const [loading, setLoading] = useState(true);
  const [directoryUsers, setDirectoryUsers] = useState<DirectoryUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<DirectoryUser[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<DirectoryUser | null>(null);
  const [playerLineups, setPlayerLineups] = useState<PlayerLineup[]>([]);
  const [loadingLineups, setLoadingLineups] = useState(false);
  const [showLineupsModal, setShowLineupsModal] = useState(false);
  const [lineupTeamFilter, setLineupTeamFilter] = useState<number | null>(null);

  useEffect(() => {
    if (!directoryEnabled) {
      showError('Directory feature is not available');
      return;
    }

    const fetchDirectory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = selectedTeamId ? { team_id: selectedTeamId } : {};
        const response = await axios.get('/users/directory', { params });
        setDirectoryUsers(response.data);
        setFilteredUsers(response.data);
      } catch (error: any) {
        console.error('Error fetching directory:', error);
        showError('Failed to load directory');
      } finally {
        setLoading(false);
      }
    };

    fetchDirectory();
  }, [user, selectedTeamId, directoryEnabled, showError]);

  useEffect(() => {
    // Filter users by search query
    let filtered = directoryUsers;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query)
      );
    }

    setFilteredUsers(filtered);
  }, [searchQuery, directoryUsers]);

  const handleUserClick = async (user: DirectoryUser) => {
    setSelectedUser(user);
    setShowLineupsModal(true);
    setLoadingLineups(true);
    setLineupTeamFilter(null);

    try {
      const params: { team_id?: number } = {};
      if (user.shared_teams.length === 1) {
        params.team_id = user.shared_teams[0].team_id;
        setLineupTeamFilter(user.shared_teams[0].team_id);
      }

      const response = await axios.get(`/users/${user.id}/lineups`, { params });
      setPlayerLineups(response.data);
    } catch (error) {
      console.error('Error fetching player lineups:', error);
      showError('Failed to load lineups');
    } finally {
      setLoadingLineups(false);
    }
  };

  const handleLineupTeamFilterChange = async (teamId: number | null) => {
    if (!selectedUser) return;

    setLineupTeamFilter(teamId);
    setLoadingLineups(true);

    try {
      const params: { team_id?: number } = {};
      if (teamId) {
        params.team_id = teamId;
      }

      const response = await axios.get(`/users/${selectedUser.id}/lineups`, { params });
      setPlayerLineups(response.data);
    } catch (error) {
      console.error('Error fetching player lineups:', error);
      showError('Failed to load lineups');
    } finally {
      setLoadingLineups(false);
    }
  };

  const isMatchInPast = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const getTeamDisplayInfo = (sharedTeams: DirectoryUser['shared_teams']): { teamName: string; additionalCount: number } | null => {
    if (sharedTeams.length === 0) {
      return null;
    }

    // Sort teams by join_date (most recent first), handling missing dates
    const sortedTeams = [...sharedTeams].sort((a, b) => {
      const dateA = a.join_date ? new Date(a.join_date).getTime() : 0;
      const dateB = b.join_date ? new Date(b.join_date).getTime() : 0;
      return dateB - dateA; // Descending order (most recent first)
    });

    return {
      teamName: sortedTeams[0].team_name,
      additionalCount: sortedTeams.length - 1
    };
  };

  if (!directoryEnabled) {
    return (
      <div>
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600 dark:text-gray-400">
            Directory feature is not available
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <Navigation />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Directory</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            View your teammates and their contact information
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              />
            </div>

            {/* Team Filter */}
            <div className="sm:w-64">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedTeamId || ''}
                  onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                >
                  <option value="">All Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Directory List */}
        {filteredUsers.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery || selectedTeamId ? 'No teammates found matching your filters' : 'No teammates found'}
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((directoryUser) => (
                <button
                  key={directoryUser.id}
                  onClick={() => handleUserClick(directoryUser)}
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-900 dark:text-gray-100 font-medium">
                      {getDisplayName(directoryUser)}
                    </div>
                    {(() => {
                      const teamInfo = getTeamDisplayInfo(directoryUser.shared_teams);
                      return teamInfo ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate flex items-center gap-1">
                          <span>{teamInfo.teamName}</span>
                          {teamInfo.additionalCount > 0 && (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">
                              + {teamInfo.additionalCount} more
                            </span>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Player Detail Modal */}
        {showLineupsModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {getDisplayName(selectedUser)}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowLineupsModal(false);
                    setSelectedUser(null);
                    setPlayerLineups([]);
                    setLineupTeamFilter(null);
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {/* Contact Information */}
                <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    {selectedUser.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</p>
                          <a 
                            href={`mailto:${selectedUser.email}`}
                            className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            {selectedUser.email}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {selectedUser.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Phone</p>
                          <a 
                            href={`tel:${selectedUser.phone.replace(/\D/g, '')}`}
                            className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            {formatPhoneNumber(selectedUser.phone)}
                          </a>
                        </div>
                      </div>
                    )}

                    {!selectedUser.email && !selectedUser.phone && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No contact information available</p>
                    )}
                  </div>
                </div>

                {/* Team Associations */}
                <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Team Associations</h3>
                  {selectedUser.shared_teams.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedUser.shared_teams.map(team => (
                        <span
                          key={team.team_id}
                          className="inline-block px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded"
                        >
                          {team.team_name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No shared teams</p>
                  )}
                </div>

                {/* Lineups Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lineups</h3>
                    {selectedUser.shared_teams.length > 1 && (
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <select
                          value={lineupTeamFilter || ''}
                          onChange={(e) => handleLineupTeamFilterChange(e.target.value ? parseInt(e.target.value) : null)}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">All Teams</option>
                          {selectedUser.shared_teams.map(team => (
                            <option key={team.team_id} value={team.team_id}>
                              {team.team_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {loadingLineups ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    </div>
                  ) : playerLineups.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        No lineups found for this player
                      </p>
                    </div>
                  ) : (
                  <div className="space-y-4">
                    {playerLineups.map((lineup) => {
                      const isPast = isMatchInPast(lineup.match_date);
                      const isWon = lineup.won === true;
                      const isLost = lineup.won === false;

                      return (
                        <Link
                          key={lineup.lineup_id}
                          to={`/matches/${lineup.match_id}`}
                          className={`block border rounded-lg p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] ${
                            isPast
                              ? 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={(e) => {
                            // Close the modal when clicking on a match
                            setShowLineupsModal(false);
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">
                                  vs {lineup.opponent_name}
                                </h3>
                                {isWon && (
                                  <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700 self-start">
                                    Won
                                  </span>
                                )}
                                {isLost && (
                                  <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-300 dark:border-red-700 self-start">
                                    Lost
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                                <Users className="w-4 h-4 flex-shrink-0" />
                                {lineup.team_name}
                              </p>
                              <div className="space-y-1">
                                {(() => {
                                  const formatted = formatMatchDateFull(lineup.match_date);
                                  return (
                                    <>
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 flex-shrink-0" />
                                        {formatted.date}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                        <Clock className="w-4 h-4 flex-shrink-0" />
                                        {formatted.time}
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Position {lineup.position_number}: </span>
                              {lineup.player1_name && (
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                  {lineup.player1_name}
                                </span>
                              )}
                              {lineup.player2_name && (
                                <span className="text-gray-900 dark:text-gray-100">
                                  {' & '}
                                  <span className="font-medium">{lineup.player2_name}</span>
                                </span>
                              )}
                            </div>
                            {lineup.score && (
                              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                Score: {lineup.score}
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectoryPage;

